import { useCallback, useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { saveAs } from "file-saver";
import { toast } from "react-toastify";
import apiClient from "../../core/config/api";
import { downloadFileName } from "../../utils/fileNames";
import { usePhoneGate } from "../../components/phone/usePhoneGate";
import ResolveCatchAllModal, { ResolveCatchAllSelection } from "../../components/ResolveCatchAllModal";
import {
    BUCKET_LABELS,
    ResultBucket,
    VerifierResultRow as ResultRow,
    VerifierStatusCount as StatusCount,
    bucketForStatus,
    fetchVerifierResults,
} from "../../services/verifierResultsService";
import "./email_verify.css";
import "./bulk_results.css";

type Bucket = ResultBucket;

// Counts handed over from the history table row, used until the results
// endpoint reports its own per-status breakdown.
interface LocationState {
    fileName?: string;
    counts?: Partial<Record<Exclude<Bucket, "other">, number>>;
}

const PAGE_SIZE = 25;

const bucketFor = bucketForStatus;
const STATUS_LABELS = BUCKET_LABELS;

const titleCase = (value: string) =>
    value
        .replace(/[_-]+/g, " ")
        .split(" ")
        .filter(Boolean)
        .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
        .join(" ");

const formatNumber = (value: number) => value.toLocaleString("en-US");

// Renders a yes/no style flag from whichever field name the backend uses.
// Returns null when the backend didn't send it, so the caller can decide.
const readFlag = (value: boolean | string | null | undefined, yes: string, no: string): string | null => {
    if (value === undefined || value === null || value === "") return null;
    if (typeof value === "boolean") return value ? yes : no;
    const lower = value.trim().toLowerCase();
    if (["true", "yes", "found", "pass", "valid"].includes(lower)) return yes;
    if (["false", "no", "not_found", "not found", "none", "missing", "fail"].includes(lower)) return no;
    return titleCase(value);
};

const catchAllCell = (row: ResultRow) => {
    const explicit = readFlag(row.catch_all ?? row.is_catch_all, "Yes", "No");
    if (explicit) return explicit;
    // A catch-all result means the domain accepts anything; a definite
    // valid/invalid answer from the mailbox means it doesn't.
    const bucket = bucketFor(row.status);
    if (bucket === "catchall") return "Yes";
    if (bucket === "valid" || bucket === "invalid") return "No";
    return "—";
};

// Plain-language reason per result, as in the design. The stored message is
// an internal note (e.g. "Reused cached verification result"), so it is only
// shown for statuses without a reason of their own.
const REASONS: Partial<Record<Bucket, string>> = {
    valid: "Email is valid and can receive emails.",
    invalid: "Mailbox does not exist.",
    catchall: "Domain accepts any address; mailbox unconfirmed.",
    donotsend: "Role-based address.",
    risky: "Could not be verified with enough confidence.",
    domain: "No valid DNS or MX records.",
};
const reasonFor = (row: ResultRow) => REASONS[bucketFor(row.status)] || row.reason || row.message || "—";

const domainOf = (row: ResultRow) => row.domain || (row.email?.includes("@") ? row.email.split("@")[1] : "") || "—";

const RESOLVE_POLL_MS = 10000;

const BulkVerificationResults = () => {
    // Free plan users verify their phone before they can see the results
    // (or download them). The server refuses them too; this shows the modal
    // instead of the table until they do, even when the URL is opened directly.
    const { phoneRequired, requirePhone, handlePhoneError, phoneModal } = usePhoneGate();
    const locked = phoneRequired === true;
    const { id } = useParams();
    const requestId = Number(id);
    const navigate = useNavigate();
    const location = useLocation();
    const state = (location.state || {}) as LocationState;

    const [rows, setRows] = useState<ResultRow[]>([]);
    const [page, setPage] = useState(1);
    const [totalPages, setTotalPages] = useState(0);
    const [statusCounts, setStatusCounts] = useState<StatusCount[]>([]);
    const [fileName, setFileName] = useState(state.fileName || "");
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [downloading, setDownloading] = useState(false);
    const [showResolveModal, setShowResolveModal] = useState(false);
    const [resolving, setResolving] = useState(false);
    // Catch-All Resolver state for this request, from the server, so it
    // survives a reload: null = never ran.
    const [resolveState, setResolveState] = useState<{
        status: "in_progress" | "completed" | "failed" | null;
        submitted: number;
        resolved: number;
    }>({ status: null, submitted: 0, resolved: 0 });

    const fetchPage = useCallback(async () => {
        if (!Number.isFinite(requestId)) {
            setError("This verification could not be found.");
            setLoading(false);
            return;
        }
        setLoading(true);
        setError(null);
        try {
            const data = await fetchVerifierResults(requestId, { page, page_size: PAGE_SIZE, include_dns: true });
            setRows(data.data || []);
            setTotalPages(data.total_pages || 0);
            if (Array.isArray(data.distinct_statuses)) setStatusCounts(data.distinct_statuses);
            if (!state.fileName && data.file_name) setFileName(data.file_name);
        } catch (err: any) {
            if (await handlePhoneError(err, () => undefined)) return; // reloads once verified
            setError(err?.response?.data?.message || "Failed to load results. Please try again.");
        } finally {
            setLoading(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [requestId, page]);

    // Results load only once we know the user may see them; a Free plan user
    // who hasn't verified gets the phone modal first (the reload follows
    // when `phoneRequired` turns false).
    useEffect(() => {
        if (phoneRequired === null) return;
        if (phoneRequired) {
            setRows([]);
            setLoading(false);
            requirePhone(() => undefined);
            return;
        }
        fetchPage();
    }, [fetchPage, phoneRequired, requirePhone]);

    const fetchResolveState = useCallback(async () => {
        if (!Number.isFinite(requestId)) return null;
        try {
            const { data } = await apiClient.get(`/verifier-requests/re/${requestId}/resolve-catch-all/status`);
            const next = { status: data?.status ?? null, submitted: Number(data?.submitted || 0), resolved: Number(data?.resolved || 0) };
            setResolveState(next);
            return next;
        } catch {
            return null; // Non-fatal - the button simply stays available.
        }
    }, [requestId]);

    useEffect(() => {
        fetchResolveState();
    }, [fetchResolveState]);

    // While the resolver runs, check every 10s; when it finishes, reload the
    // table and counts so the new verdicts show.
    useEffect(() => {
        if (resolveState.status !== "in_progress") return;
        const timer = window.setInterval(async () => {
            const next = await fetchResolveState();
            if (next && next.status !== "in_progress") fetchPage();
        }, RESOLVE_POLL_MS);
        return () => window.clearInterval(timer);
    }, [resolveState.status, fetchResolveState, fetchPage]);

    const summary = useMemo(() => {
        const totals: Record<Exclude<Bucket, "other">, number> = {
            valid: 0, invalid: 0, catchall: 0, donotsend: 0, domain: 0, risky: 0,
        };
        let verified = 0;
        if (statusCounts.length > 0) {
            statusCounts.forEach(({ key, count }) => {
                const n = Number(count || 0);
                verified += n;
                const bucket = bucketFor(key);
                if (bucket !== "other") totals[bucket] += n;
            });
        } else if (state.counts) {
            (Object.keys(totals) as Exclude<Bucket, "other">[]).forEach((bucket) => {
                totals[bucket] = Number(state.counts?.[bucket] || 0);
                verified += totals[bucket];
            });
        }
        return { verified, ...totals };
    }, [statusCounts, state.counts]);

    const resolvableCount = summary.catchall;

    const downloadReport = async () => {
        if (downloading) return;
        setDownloading(true);
        try {
            const response = await apiClient.get(`/verifier-requests/re/${requestId}/download-results`, {
                responseType: "blob",
            });
            // Always a clean ".csv" name - see utils/fileNames.
            const filename = downloadFileName(
                fileName || response.headers["content-disposition"]?.split("filename=")[1]?.replace(/"/g, ""),
                `verification_results_${requestId}`
            );
            saveAs(response.data, filename);
        } catch (err: any) {
            if (await handlePhoneError(err, () => downloadReport())) return;
            toast.error(err?.response?.data?.message || "Failed to download the report. Please try again.");
        } finally {
            setDownloading(false);
        }
    };

    const confirmResolve = async (selection?: ResolveCatchAllSelection) => {
        if (resolving) return;
        setResolving(true);
        try {
            // Customer route: paid from the customer's own credits, owner-only.
            await apiClient.patch(`/verifier-requests/re/${requestId}/resolve-catch-all`, selection ?? {});
            toast.success("Catch-All emails queued for resolution");
            setResolveState((prev) => ({ ...prev, status: "in_progress" }));
            setShowResolveModal(false);
            fetchResolveState();
        } catch (err: any) {
            toast.error(
                err?.response?.data?.message ||
                err?.response?.data?.detail ||
                "Failed to queue Catch-All resolution. Please try again."
            );
        } finally {
            setResolving(false);
        }
    };

    const stats: { key: string; label: string; value: number; tone: string }[] = [
        { key: "verified", label: "Emails Verified", value: summary.verified, tone: "total" },
        { key: "valid", label: "Valid Emails", value: summary.valid, tone: "valid" },
        { key: "invalid", label: "Invalid Emails", value: summary.invalid, tone: "invalid" },
        { key: "catchall", label: "Catch-All", value: summary.catchall, tone: "catchall" },
        { key: "donotsend", label: "Do Not Mail", value: summary.donotsend, tone: "donotsend" },
        { key: "domain", label: "Invalid Domain", value: summary.domain, tone: "domain" },
        { key: "risky", label: "Risky", value: summary.risky, tone: "risky" },
    ];

    const pageNumbers = useMemo(() => {
        if (totalPages <= 5) return Array.from({ length: totalPages }, (_, i) => i + 1);
        if (page <= 3) return [1, 2, 3, "...", totalPages];
        if (page >= totalPages - 2) return [1, "...", totalPages - 2, totalPages - 1, totalPages];
        return [1, "...", page, "...", totalPages];
    }, [page, totalPages]);

    return (
        <div className="bulk-results-page">
            {phoneModal}
            <button type="button" className="bulk-results-back" onClick={() => navigate("/email-verifier?tab=bulk")}>
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">
                    <path d="M10 3 5 8l5 5" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
                Back
            </button>

            <div className="bulk-results-layout">
                <div className="bulk-results-main">
                <h1 className="bulk-results-title">Bulk Verification Results</h1>

                <section className="bulk-results-card">
                    <div className="bulk-results-card-header">
                        <div className="bulk-results-file">
                            <img src="/images/pdf.svg" alt="" width={16} height={17} />
                            <span title={fileName}>{fileName || `Request #${requestId}`}</span>
                        </div>

                        <div className="bulk-results-actions">
                            <button type="button" className="bulk-results-download" onClick={downloadReport} disabled={downloading}>
                                {downloading ? (
                                    <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true" />
                                ) : (
                                    <svg width="14" height="14" viewBox="0 0 12 12" aria-hidden="true">
                                        <path d="M6.54 6.62V1.18a.57.57 0 1 0-1.14 0v5.44L3.53 4.75a.57.57 0 1 0-.8.8L5.97 8.8l3.24-3.24a.57.57 0 1 0-.8-.8Z" fill="currentColor" />
                                        <path d="M.57 7.38a.57.57 0 0 1 .57.57v.91c0 .44.36.8.8.8h6.36c.44 0 .8-.36.8-.8v-.91a.57.57 0 0 1 1.14 0v.91c0 1.07-.87 1.93-1.93 1.93H1.93A1.93 1.93 0 0 1 0 8.86v-.91c0-.31.25-.57.57-.57Z" fill="currentColor" transform="translate(.9 .6)" />
                                    </svg>
                                )}
                                Download Full Report
                            </button>

                            <span className="bulk-results-actions-divider" />

                            {resolveState.status === "in_progress" ? (
                                <span className="bulk-results-resolving" role="status">
                                    <span className="spinner-border spinner-border-sm" aria-hidden="true" />
                                    Resolving
                                    {resolveState.submitted > 0 && (
                                        <span className="bulk-results-resolving-count">
                                            {resolveState.resolved.toLocaleString("en-US")}/{resolveState.submitted.toLocaleString("en-US")}
                                        </span>
                                    )}
                                </span>
                            ) : resolveState.status === "completed" ? (
                                <span className="bulk-results-resolved" role="status">
                                    <svg width="14" height="14" viewBox="0 0 16 16" aria-hidden="true">
                                        <circle cx="8" cy="8" r="8" fill="currentColor" />
                                        <path d="m4.8 8.3 2.1 2.1 4.3-4.6" fill="none" stroke="#DBFFEC" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
                                    </svg>
                                    Resolved
                                </span>
                            ) : (
                                <button
                                    type="button"
                                    className="bulk-results-resolver"
                                    onClick={() => {
                                        if (!requirePhone(() => setShowResolveModal(true))) setShowResolveModal(true);
                                    }}
                                    disabled={resolvableCount === 0}
                                    title={resolvableCount === 0 ? "No Catch-All emails to resolve" : undefined}
                                >
                                    Catch-All Resolver
                                </button>
                            )}
                        </div>
                    </div>

                    {error ? (
                        <p className="bulk-upload-error-text">{error}</p>
                    ) : (
                        <div className="bulk-results-table-wrapper">
                            <table className="bulk-results-table">
                                <thead>
                                    <tr>
                                        <th>Email</th>
                                        <th>Status</th>
                                        <th>Reason</th>
                                        <th>Domain</th>
                                        <th>Catch-All</th>
                                        <th>SPF</th>
                                        <th>DMARC</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {locked ? (
                                        <tr>
                                            <td colSpan={7} className="bulk-results-placeholder">
                                                <div className="bulk-results-locked">
                                                    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                                                        <rect x="5" y="11" width="14" height="10" rx="2" stroke="currentColor" strokeWidth="1.6" />
                                                        <path d="M8 11V8a4 4 0 0 1 8 0v3" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
                                                    </svg>
                                                    <p>Verify your phone number to view these results.</p>
                                                    <button type="button" onClick={() => requirePhone(() => undefined)}>
                                                        Verify phone number
                                                    </button>
                                                </div>
                                            </td>
                                        </tr>
                                    ) : loading ? (
                                        <tr>
                                            <td colSpan={7} className="bulk-results-placeholder">
                                                <div className="spinner-border" role="status">
                                                    <span className="visually-hidden">Loading...</span>
                                                </div>
                                            </td>
                                        </tr>
                                    ) : rows.length === 0 ? (
                                        <tr>
                                            <td colSpan={7} className="bulk-results-placeholder">No results available yet.</td>
                                        </tr>
                                    ) : (
                                        rows.map((row, index) => {
                                            const bucket = bucketFor(row.status);
                                            const label = STATUS_LABELS[bucket] || titleCase(row.status || "Unknown");
                                            const noDns = bucket === "domain";
                                            return (
                                                <tr key={`${row.email}-${index}`}>
                                                    <td>
                                                        <span className="bulk-results-email" title={row.email || ""}>{row.email || "—"}</span>
                                                    </td>
                                                    <td>
                                                        <span className={`bulk-results-status status-${bucket}`}>{label}</span>
                                                    </td>
                                                    <td className="bulk-results-reason">{reasonFor(row)}</td>
                                                    <td>{domainOf(row)}</td>
                                                    <td>{noDns ? "—" : catchAllCell(row)}</td>
                                                    <td>{noDns ? "—" : readFlag(row.spf ?? row.has_spf, "Found", "Not Found") ?? "—"}</td>
                                                    <td>{noDns ? "—" : readFlag(row.dmarc ?? row.has_dmarc, "Found", "Not Found") ?? "—"}</td>
                                                </tr>
                                            );
                                        })
                                    )}
                                </tbody>
                            </table>
                        </div>
                    )}

                    {totalPages > 1 && (
                        <div className="pagination-container">
                            <button className="pagination-arrow" onClick={() => setPage((p) => p - 1)} disabled={page === 1 || loading}>
                                &lt;
                            </button>
                            {pageNumbers.map((p, index) =>
                                p === "..." ? (
                                    <span key={`ellipsis-${index}`} className="pagination-ellipsis">...</span>
                                ) : (
                                    <button
                                        key={p}
                                        className={`pagination-number ${page === p ? "active" : ""}`}
                                        onClick={() => setPage(p as number)}
                                        disabled={loading}
                                    >
                                        {p}
                                    </button>
                                )
                            )}
                            <button className="pagination-arrow" onClick={() => setPage((p) => p + 1)} disabled={page === totalPages || loading}>
                                &gt;
                            </button>
                        </div>
                    )}
                </section>
                </div>

                <aside className="bulk-results-summary" aria-label="Verification summary">
                    {stats.map((stat) => (
                        <div key={stat.key} className="bulk-results-stat">
                            <p className="bulk-results-stat-label">{stat.label}</p>
                            <p className={`bulk-results-stat-value tone-${stat.tone}`}>{formatNumber(stat.value)}</p>
                        </div>
                    ))}
                </aside>
            </div>

            {showResolveModal && (
                <ResolveCatchAllModal
                    requestId={requestId}
                    emailCount={resolvableCount}
                    submitting={resolving}
                    onClose={() => setShowResolveModal(false)}
                    onConfirm={confirmResolve}
                />
            )}
        </div>
    );
};

export default BulkVerificationResults;
